import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Shield, Target, CheckCircle, ArrowLeft, Database, Phone, Mail } from 'lucide-react';

const Contactability = () => {
  const capabilities = [
    {
      icon: Database,
      title: "Data Verification & Enrichment",
      description: "Advanced algorithms verify and enhance contact data quality, ensuring accuracy and completeness."
    },
    {
      icon: Target,
      title: "Contact Quality Scoring", 
      description: "Intelligent scoring system evaluates the likelihood of successful contact for each data point."
    },
    {
      icon: Phone,
      title: "Channel & Response Prediction",
      description: "AI-powered insights predict the most effective communication channels and optimal timing."
    },
    {
      icon: Mail,
      title: "CRM Integration & Automation",
      description: "Seamless integration with existing CRM systems for automated data updates and workflows."
    }
  ];

  const benefits = [
    "Verified and accurate customer contact data",
    "Higher engagement rates through optimal channel selection",
    "Reduced marketing waste and improved ROI",
    "Enhanced compliance tracking and audit trails",
    "Real-time data quality monitoring",
    "Automated contact preference management"
  ];

  const metrics = [
    { value: "40%", label: "Improvement in Contact Rates" },
    { value: "60%", label: "Reduction in Bounce Rates" },
    { value: "85%", label: "Data Accuracy Achievement" },
    { value: "30%", label: "Cost Reduction in Outreach" }
  ];

  return (
    <div className="dark-theme pt-24">
      {/* Breadcrumb */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 text-gray-400">
            <Link to="/solutions" className="hover:text-bluemp-400 transition-colors">Solutions</Link>
            <span>/</span>
            <span className="text-white">Contactability AI Solution</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors mb-6">
                <ArrowLeft size={16} />
                Back to Solutions
              </Link>
              
              <h1 className="display-large text-white mb-6">
                Reach the Right People,{' '}
                <span className="text-bluemp-400">the Right Way</span>
              </h1>
              
              <p className="body-large text-gray-300 mb-8">
                Transform your customer outreach with verified, enriched contact data and intelligent channel optimization. Our Contactability AI Solution ensures you reach more customers effectively while maintaining full compliance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Improve Your Contactability
                  <ArrowRight size={20} />
                </Link>
                <Link to="/experience" className="btn-secondary">
                  View Results
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <Users size={64} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4">Smart Contact Intelligence</h3>
                <p className="body-medium text-gray-300 mb-6">
                  Leverage AI to verify, enrich, and optimize your customer contact data for maximum engagement and compliance.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {metrics.slice(0, 2).map((metric, index) => (
                    <div key={index} className="text-center p-3 bg-bluemp-400/10 rounded">
                      <div className="heading-3 text-bluemp-400">{metric.value}</div>
                      <div className="body-small text-gray-300">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Core Capabilities
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Advanced AI technologies that transform raw contact data into actionable intelligence for improved customer engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <div key={index} className="group dark-hover bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <capability.icon size={48} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {capability.title}
                </h3>
                <p className="body-medium text-gray-300">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              How It Works
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              A comprehensive process that transforms your contact data into a powerful engagement asset.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Data Ingestion",
                description: "Securely import your existing contact data from multiple sources and systems."
              },
              {
                step: "02", 
                title: "AI Verification",
                description: "Advanced algorithms verify and validate contact information for accuracy and compliance."
              },
              {
                step: "03",
                title: "Smart Enrichment",
                description: "Enrich contact data with additional information and predict optimal communication preferences."
              },
              {
                step: "04",
                title: "Intelligent Deployment",
                description: "Deploy verified, enriched data back to your systems with real-time quality monitoring."
              }
            ].map((phase, index) => (
              <div key={index} className="text-center group dark-hover">
                <div className="w-16 h-16 bg-bluemp-400/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-bluemp-400/30 transition-colors">
                  <span className="heading-2 text-bluemp-400">{phase.step}</span>
                </div>
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">{phase.title}</h3>
                <p className="body-small text-gray-300">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Results */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Key Benefits */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Shield size={32} className="text-bluemp-400" />
                <h2 className="display-medium text-white">Key Benefits</h2>
              </div>
              
              <p className="body-large text-gray-300 mb-8">
                Experience measurable improvements in customer engagement while maintaining the highest standards of data quality and compliance.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-medium text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Performance Metrics */}
            <div>
              <h2 className="display-medium text-white mb-8">Proven Results</h2>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                {metrics.map((metric, index) => (
                  <div key={index} className="text-center p-6 bg-gray-900/50 backdrop-blur-sm rounded-lg border border-bluemp-400/20">
                    <div className="display-large text-bluemp-400 mb-2">{metric.value}</div>
                    <div className="body-small text-gray-300">{metric.label}</div>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-gray-900/50 backdrop-blur-sm rounded-lg border border-bluemp-400/20">
                <h3 className="heading-3 text-bluemp-400 mb-4">Client Success Story</h3>
                <p className="body-medium text-gray-300 mb-4">
                  "Blue MP's Contactability solution transformed our customer engagement. We saw a 40% improvement in contact rates and significantly reduced our compliance risks."
                </p>
                <div className="text-bluemp-400 body-small">
                  - Head of Operations, Major Financial Services Firm
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Technical Features
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Advanced AI and machine learning capabilities that deliver superior contact data intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Real-time Processing",
                description: "Process and verify contact data in real-time for immediate deployment and use."
              },
              {
                title: "Multi-source Integration", 
                description: "Integrate data from multiple sources including CRMs, databases, and external providers."
              },
              {
                title: "Compliance Management",
                description: "Built-in compliance features ensure adherence to POPIA, GDPR, and industry regulations."
              },
              {
                title: "Predictive Analytics",
                description: "Advanced models predict contact success probability and optimal engagement timing."
              },
              {
                title: "Quality Monitoring",
                description: "Continuous monitoring and reporting on data quality metrics and performance indicators."
              },
              {
                title: "API Integration",
                description: "Robust APIs for seamless integration with existing systems and workflows."
              }
            ].map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-bluemp-400/20 dark-hover">
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">{feature.title}</h3>
                <p className="body-small text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Improve Your Customer Contactability?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Transform your customer engagement with intelligent contact data management. Let's discuss how our solution can improve your outreach effectiveness.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Get Started Today
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions/broker-analytics" className="btn-secondary">
              Explore Broker Analytics
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contactability;